import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form as FormBootstrap } from 'react-bootstrap';
import cx from 'classnames';
import Icon from '../../../../../../../modules/react/components/stateless/Icon';
import Button from '../../../../../../../modules/react/components/stateless/Button';
import styles from '../Actions.scss';
import { STOREDGE } from '../../../../../../../constants/softwareProviders';
import { TRACK_SUBMIT_PROMO_CODE } from '../../../../../../../src/web/utils/dataLayer';
import useEventTrigger from '../../../../../../../modules/react/hooks/useEventTrigger';

const PromoCode = ({
  facilityId,
  discountPlans,
  onPromoUpdate,
  appliedPromos,
  softwareProvider, // New prop for conditional display
  primaryColorClass,
  secondaryColorHoverClass,
  secondaryColorBackgroundClass,
  primaryColorBackgroundHoverClass,
  buttonStyleClass,
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [addPromo, setAddPromo] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleTextChange = (event) => {
    setPromoCode(event.target.value.toUpperCase());
  };

  const { addEvent } = useEventTrigger();

  const isAlreadyApplied = selectedPlan => appliedPromos.some(
    applied => applied.name === selectedPlan.name,
  );

  const getAnalyticsPayload = () => ({
    facilityId,
    eventLocation: 'reservationForm',
    discountAttempted: true,
    // geoLocation: undefined, // will implement later
    timeStamp: Date.now(),
  });

  const handleApplyClick = () => {
    const selectedPlan = discountPlans.find((plan) => {
      const isMatchingPlan = plan.name === promoCode;
      const isManualApply = !plan.autoApply;
      const isActive = plan.turnedOn;

      // Safely handle null or undefined startDate and endDate
      const isWithinDateRange = (!plan.startDate || new Date(plan.startDate) <= new Date())
        && (!plan.endDate || new Date(plan.endDate) >= new Date());

      return isMatchingPlan && isManualApply && isActive && isWithinDateRange;
    });

    const defaultPayload = getAnalyticsPayload();
    let discountApplied;

    if (selectedPlan && !isAlreadyApplied(selectedPlan)) {
      onPromoUpdate(selectedPlan);
      setShowError(false);
      setAddPromo(false);
      discountApplied = true;
    } else {
      setShowError(true);
      onPromoUpdate(null);
      discountApplied = false;
    }
    const analyticsPayload = {
      discountApplied,
      ...defaultPayload,
    };

    addEvent(TRACK_SUBMIT_PROMO_CODE, analyticsPayload, 'click');
  };

  const toggleAddPromo = () => {
    setAddPromo(!addPromo);
    setShowError(false); // Reset error on toggle
  };

  // Render the promo code input only if the software provider is STOREDGE
  if (softwareProvider !== STOREDGE) {
    return null;
  }

  return (
    <div>
      <Col className={styles.promoText}>
        <Button
          variant="primary"
          data-testid="add-promo"
          className={styles.addPromoButton}
          onClick={toggleAddPromo}
        >
          <Icon
            id={addPromo ? 'draft' : 'add'}
            className={cx(
              secondaryColorHoverClass,
              !addPromo && [primaryColorClass, secondaryColorHoverClass],
            )}
            label={addPromo ? 'Close' : 'Add Promo'}
          />{' '}
          Add promo code
        </Button>
      </Col>
      {addPromo && (
        <div className={styles.promoContainer}>
          <div className={styles.promoInputContainer}>
            <div className={styles.promoCodeInput}>
              <FormBootstrap.Control
                value={promoCode}
                placeholder="Add promo code"
                id="reservationForm-addPromo"
                name="addPromo"
                onChange={handleTextChange}
              />
            </div>
            <div>
              <Button
                variant="primary"
                className={cx(
                  styles.applyButton,
                  secondaryColorBackgroundClass,
                  primaryColorBackgroundHoverClass,
                  buttonStyleClass,
                )}
                onClick={handleApplyClick}
                style={{ fontSize: '13px', lineHeight: '23px' }}
              >
                Apply
              </Button>
            </div>
          </div>
          <Col>
            {showError && (
              <div className={styles.error}>
                <Icon id="warning" label="Invalid Promo Code" /> Invalid promo
                code
              </div>
            )}
          </Col>
        </div>
      )}
    </div>
  );
};

PromoCode.propTypes = {
  facilityId: PropTypes.string,
  discountPlans: PropTypes.array,
  onPromoUpdate: PropTypes.func.isRequired,
  appliedPromos: PropTypes.array,
  softwareProvider: PropTypes.string, // New prop type for softwareProvider
  primaryColorClass: PropTypes.string,
  secondaryColorHoverClass: PropTypes.string,
  secondaryColorBackgroundClass: PropTypes.string,
  primaryColorBackgroundHoverClass: PropTypes.string,
  buttonStyleClass: PropTypes.string,
};

PromoCode.defaultProps = {
  facilityId: undefined,
  discountPlans: [],
  appliedPromos: [],
  softwareProvider: '',
  primaryColorClass: '',
  secondaryColorHoverClass: '',
  secondaryColorBackgroundClass: '',
  primaryColorBackgroundHoverClass: '',
  buttonStyleClass: undefined,
};

export default PromoCode;
